import React, { useState } from 'react';
import { CheckCircle2, MessageCircle, Activity, ChevronDown, BellRing } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ActivityFeed } from '@/components/activity-feed/ActivityFeed';
import type { NotificationFeedItem } from '@om/shared-types';

interface PendingTask {
  id: string;
  title: string;
  context?: string;
  dueDate?: string;
  celebrationId?: number;
  eventId?: number;
}

interface RecentMessage {
  id: string;
  userId: string;
  userName: string;
  time: string;
  message: string;
  hasImage?: boolean;
  channelCid?: string;  // NEW: For navigating to Stream Chat
  celebrationId?: number;   // NEW: Context for celebration chats
}

interface RecentActivityItem {
  id: string;
  type: string;
  title: string;
  description: string;
  icon: string;
}

interface RightPanelProps {
  pendingTasks?: PendingTask[];
  recentMessages?: RecentMessage[];
  recentActivity?: RecentActivityItem[];
  notifications?: NotificationFeedItem[];
  onMarkAllNotificationsRead?: () => void;
  onMarkNotificationRead?: (recipientId: number) => void;
  className?: string;
}

export const RightPanel: React.FC<RightPanelProps> = ({
  pendingTasks = [],
  recentMessages = [],
  notifications = [],
  onMarkAllNotificationsRead,
  onMarkNotificationRead,
  className
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const toggleMessageExpansion = (msgId: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const handleOpenChat = (msg: RecentMessage) => {
    if (msg.channelCid) {
      navigate(`/chat?channel=${encodeURIComponent(msg.channelCid)}`);
      return;
    }

    navigate('/chat');
  };

  const getTaskStyle = (dueDate?: string) => {
    if (!dueDate) return { bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400', text: '' };
    
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    const threeDays = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    threeDays.setDate(threeDays.getDate() + 3);
    
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    threeDays.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date < today) {
      // Overdue - Red
      return { bg: 'bg-red-50', border: 'border-red-300', dot: 'bg-red-500', text: t('layout.rightPanel.taskStatus.overdue'), textColor: 'text-red-600' };
    } else if (date.getTime() === today.getTime()) {
      // Due today - Orange
      return { bg: 'bg-orange-50', border: 'border-orange-300', dot: 'bg-orange-500', text: t('layout.rightPanel.taskStatus.today'), textColor: 'text-orange-600' };
    } else if (date.getTime() === tomorrow.getTime()) {
      // Due tomorrow - Yellow
      return { bg: 'bg-yellow-50', border: 'border-yellow-300', dot: 'bg-yellow-500', text: t('layout.rightPanel.taskStatus.tomorrow'), textColor: 'text-yellow-700' };
    } else if (date <= threeDays) {
      // Due within 3 days - Blue
      const days = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-400', text: `${days}d`, textColor: 'text-blue-600' };
    } else {
      // Due later - Gray
      const days = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return { bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400', text: `${days}d`, textColor: 'text-gray-500' };
    }
  };

  const handleTaskClick = (task: PendingTask) => {
    if (task.eventId) {
      navigate(`/events/${task.eventId}/tasks`, { state: { highlightTaskId: task.id } });
    } else if (task.celebrationId) {
      navigate(`/celebrations/${task.celebrationId}/tasks`, { state: { highlightTaskId: task.id } });
    }
  };

  const formatNotificationTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const unreadCount = notifications.filter((item) => !item.readAt).length;
  const visibleNotifications = showUnreadOnly
    ? notifications.filter((item) => !item.readAt)
    : notifications;

  return (
    <div className={cn('flex flex-col gap-3 p-3', className)}>
      <div className="p-3 rounded-lg border-2 border-sky-200 bg-sky-50/40">
        <div className="flex items-center gap-2 mb-2">
          <BellRing className="h-4 w-4 text-sky-600" />
          <h3 className="font-semibold text-xs text-sky-800">{t('layout.rightPanel.notifications.title')}</h3>
          <span className="text-[10px] text-sky-600 ml-auto">{unreadCount}/{notifications.length}</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Button
            size="sm"
            variant={showUnreadOnly ? 'default' : 'outline'}
            className="h-6 text-[10px] px-2"
            onClick={() => setShowUnreadOnly((prev) => !prev)}
          >
            {showUnreadOnly
              ? t('layout.rightPanel.notifications.showingUnread')
              : t('layout.rightPanel.notifications.unreadOnly')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[10px] px-2"
            onClick={onMarkAllNotificationsRead}
            disabled={unreadCount === 0}
          >
            {t('layout.rightPanel.notifications.markAllRead')}
          </Button>
        </div>
        <div className="space-y-2">
          {visibleNotifications.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('layout.rightPanel.notifications.empty')}</p>
          ) : (
            visibleNotifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  'rounded-md border px-2.5 py-2',
                  notification.readAt ? 'border-sky-100 bg-white' : 'border-sky-300 bg-sky-50/70',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                    {notification.triggerType.replace(/_/g, ' ')}
                  </span>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {formatNotificationTime(notification.createdAt)}
                  </span>
                </div>
                <div className="text-xs font-medium text-slate-900">{notification.title}</div>
                <div className="mt-1 text-xs text-slate-600">{notification.message}</div>
                {!notification.readAt && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2"
                      onClick={() => onMarkNotificationRead?.(notification.id)}
                    >
                      {t('layout.rightPanel.notifications.markRead')}
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pending Tasks Section - Compact */}
      <div className="p-2 rounded-lg border border-orange-200 bg-orange-50/30">
        <div className="flex items-center gap-1.5 mb-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-orange-500" />
          <h3 className="font-semibold text-[11px] text-orange-700">{t('layout.rightPanel.pendingTasks.title')}</h3>
          <span className="text-[10px] text-orange-500 ml-auto">{pendingTasks.length}</span>
        </div>
        <div className="space-y-1">
          {pendingTasks.length === 0 ? (
            <p className="text-[10px] text-muted-foreground py-1">{t('layout.rightPanel.pendingTasks.empty')}</p>
          ) : (
            pendingTasks.map((task) => {
              const style = getTaskStyle(task.dueDate);
              return (
                <div 
                  key={task.id} 
                  className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer transition-all hover:shadow-sm ${style.bg} ${style.border}`}
                  onClick={() => handleTaskClick(task)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTaskClick(task); }}
                >
                  <div className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                  <span className="text-[11px] text-gray-700 truncate flex-1">{task.title}</span>
                  {style.text && (
                    <span className={`text-[9px] font-medium ${style.textColor} flex-shrink-0`}>
                      {style.text}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Messages Section - Blue border */}
      <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50/30">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="h-4 w-4 text-blue-500" />
          <h3 className="font-semibold text-xs text-blue-700">{t('layout.rightPanel.recentMessages.title')}</h3>
        </div>
        <div className="space-y-2">
          {recentMessages.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('layout.rightPanel.recentMessages.empty')}</p>
          ) : (
            recentMessages.map((msg) => (
              <div
                key={msg.id}
                className="flex items-start gap-2 cursor-pointer hover:bg-blue-100/50 p-2 rounded transition-colors"
                onClick={() => toggleMessageExpansion(msg.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMessageExpansion(msg.id); }}
              >
                <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-medium text-blue-600">
                    {msg.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-medium">{msg.userName}</span>
                    <span className="text-[10px] text-muted-foreground">{msg.time}</span>
                    {msg.hasImage && (
                      <span className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">
                        img
                      </span>
                    )}
                    <ChevronDown
                      className={cn(
                        "h-3 w-3 ml-auto transition-transform flex-shrink-0",
                        expandedMessages.has(msg.id) && "rotate-180"
                      )}
                    />
                  </div>
                  <p
                    className={cn(
                      "text-xs text-gray-600",
                      !expandedMessages.has(msg.id) && "line-clamp-2"
                    )}
                  >
                    {msg.message}
                  </p>
                  {expandedMessages.has(msg.id) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenChat(msg);
                      }}
                      className="mt-2 h-7 text-xs"
                    >
                      <MessageCircle className="h-3 w-3 mr-1" />
                      {t('layout.rightPanel.recentMessages.openChat')}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Activity Section - Green border */}
      <div className="rounded-lg border-2 border-green-200 bg-green-50/30 overflow-hidden">
        <div className="flex items-center gap-2 p-3 pb-2">
          <Activity className="h-4 w-4 text-green-500" />
          <h3 className="font-semibold text-xs text-green-700">{t('layout.rightPanel.recentActivity.title')}</h3>
        </div>
        <div className="bg-white">
          <ActivityFeed 
            showUserActivities={true} 
            pageSize={5} 
            autoRefresh={true}
            refreshInterval={30000}
          />
        </div>
      </div>
    </div>
  );
};
